import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LayoutInside from '@/layout/LayoutInside';
import Title from '@/components/shared/Title/Title';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import CTATable from '@/components/shared/TableListCustom/CTATable';
import DialogDelete from '@/components/shared/DialogDelete/DialogDelete';
import { toast } from '@/hooks/use-toast';
import { Edit, Loader2, Plus, Trash } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { usePermissions } from '@/auth/PermissionsContext';
import { AxiosErrorResponse } from '@/@types/Errors';
import {
  APPLY_TO_ALL_CURRENCIES,
  ShippingConfiguration,
  ShippingConfigurationPayload,
} from '@/@types/ShippingConfigurations';
import { currencyOptions } from '@/lib/select-options';
import { ShippingConfigurationsService } from '@/services/shipping-configurations.service';
import {
  getCountryLabelByAlpha2,
  normalizeCountryToAlpha2,
} from '@/utils/shippingCountries';

import ShippingConfigurationForm from './partials/ShippingConfigurationForm';
import ShippingNamedItemsCell from './partials/ShippingNamedItemsCell';
import ShippingStringListCell from './partials/ShippingStringListCell';
import Badge from '@/components/shared/Badge/Badge';

const LIST_LIMIT = 20;

const MotionDiv = motion.div;
const MotionTableRow = motion(TableRow);

const ROW_STAGGER_DELAY = 0.08;

function getErrorMessage(error: unknown, fallback: string) {
  const axiosError = error as AxiosError<AxiosErrorResponse>;
  return axiosError?.response?.data?.message ?? fallback;
}

export default function ShipmentsConfigurations() {
  const { t } = useTranslation(['common', 'checkouts']);
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const canCreate = hasPermission(
    'shipping.create_shipping_configuration',
  );
  const canEdit = hasPermission(
    'shipping.edit_shipping_configuration',
  );
  const canDelete = hasPermission(
    'shipping.delete_shipping_configuration',
  );
  const [currentPage, setCurrentPage] = useState(1);

  const formatCurrencyLabel = useCallback((code: string) => {
    const option = currencyOptions.find(
      (item) => String(item.value) === code.toUpperCase(),
    );
    return option ? String(option.label) : code;
  }, []);

  const formatCountryLabel = useCallback(
    (code: string) =>
      getCountryLabelByAlpha2(normalizeCountryToAlpha2(code)),
    [],
  );

  const {
    data: listResponse,
    isPending: isLoadingList,
    isError: isListError,
    error: listError,
  } = useQuery({
    queryKey: ['shipping-configurations', currentPage],
    queryFn: () =>
      ShippingConfigurationsService.list({
        page: currentPage,
        limit: LIST_LIMIT,
      }),
    staleTime: 1000 * 60,
  });

  const configurations: ShippingConfiguration[] = listResponse?.data ?? [];
  const lastPage = listResponse?.meta?.last_page ?? 1;

  const tableData = useMemo(() => {
    if (!listResponse) {
      return {
        data: [] as ShippingConfiguration[],
        meta: { last_page: 1 },
        links: {},
      };
    }
    return {
      data: configurations,
      meta: { last_page: lastPage },
      links: listResponse.links ?? {},
    };
  }, [configurations, lastPage, listResponse]);

  const headers = useMemo(
    () => [
      { title: t('common:name_single'), key: 'name' },
      { title: t('common:products'), key: 'products' },
      { title: t('common:offers'), key: 'offers' },
      { title: t('common:currency'), key: 'currencies' },
      { title: t('common:countries'), key: 'countries' },
      { title: t('common:quantity'), key: 'minQuantity' },
      { title: t('common:value'), key: 'amount' },
      { title: t('common:status'), key: 'status' },
      { title: '', key: 'cta' },
    ],
    [t],
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedConfiguration, setSelectedConfiguration] =
    useState<ShippingConfiguration | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<ShippingConfiguration | null>(null);

  useEffect(() => {
    if (isListError && listError) {
      toast({
        variant: 'error',
        description: getErrorMessage(listError, t('common:data_listing_error')),
      });
    }
  }, [isListError, listError, t]);

  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: ['shipping-configurations'] });
  };

  const { mutateAsync: createConfiguration, isPending: isCreating } =
    useMutation({
      mutationFn: (payload: ShippingConfigurationPayload) =>
        ShippingConfigurationsService.create(payload),
      onSuccess: () => {
        setCurrentPage(1);
        invalidateList();
        toast({ variant: 'success', description: t('common:shipping_configurations_created') });
      },
      onError: (error) => {
        const axiosError = error as AxiosError;
        const description =
          axiosError?.response?.status === 409
            ? t('common:shipping_configuration_conflict')
            : getErrorMessage(error, t('common:generic_error'));
        toast({
          variant: 'error',
          description,
        });
      },
    });

  const { mutateAsync: updateConfiguration, isPending: isUpdating } =
    useMutation({
      mutationFn: ({
        id,
        payload,
      }: {
        id: string;
        payload: ShippingConfigurationPayload;
      }) => ShippingConfigurationsService.update(id, payload),
      onSuccess: () => {
        invalidateList();
        toast({ variant: 'success', description: t('common:shipping_configurations_updated') });
      },
      onError: (error) => {
        const axiosError = error as AxiosError;
        const description =
          axiosError?.response?.status === 409
            ? t('common:shipping_configuration_conflict')
            : getErrorMessage(error, t('common:generic_error'));
        toast({
          variant: 'error',
          description,
        });
      },
    });

  const { mutateAsync: deleteConfiguration, isPending: isDeleting } =
    useMutation({
      mutationFn: (id: string) => ShippingConfigurationsService.delete(id),
      onSuccess: () => {
        invalidateList();
        toast({ variant: 'success', description: t('common:shipping_configurations_deleted') });
      },
      onError: (error) => {
        toast({
          variant: 'error',
          description: getErrorMessage(error, t('common:generic_error')),
        });
      },
    });

  const openCreate = () => {
    setDrawerMode('create');
    setSelectedConfiguration(null);
    setDrawerOpen(true);
  };

  const openEdit = (configuration: ShippingConfiguration) => {
    setDrawerMode('edit');
    setSelectedConfiguration(configuration);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedConfiguration(null);
  };

  const openDelete = (configuration: ShippingConfiguration) => {
    setDeleteTarget(configuration);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteConfiguration(deleteTarget.id);
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleSubmit = async (payload: ShippingConfigurationPayload) => {
    if (drawerMode === 'create') {
      await createConfiguration(payload);
      return;
    }
    if (!selectedConfiguration) return;
    await updateConfiguration({ id: selectedConfiguration.id, payload });
  };

  const isSaving = isCreating || isUpdating;

  const emptyFallback = (
    <div className="flex min-h-[320px] w-full flex-col items-center justify-center text-center space-y-4 px-4 py-10">
      <div className="flex flex-col items-center max-w-[328px]">
        <span className="text-zinc-400 text-sm font-semibold">
          {t('common:shipping_configurations_empty_title')}
        </span>
        <p className="text-[#A1A1AA] mt-2 text-sm">
          {t('common:shipping_configurations_empty_description')}
        </p>
      </div>
      <Button variant="default" onClick={openCreate} disabled={!canCreate}>
        <Plus />
        {t('common:shipping_configurations_create')}
      </Button>
    </div>
  );

  return (
    <LayoutInside
      items={[
        { text: t('common:products'), link: '/products' },
        { text: t('common:shipping_configurations_title'), link: '/products/shipments' },
      ]}
    >
      <div className="text-white min-h-screen">
        <CustomSheet open={drawerOpen} onClose={closeDrawer}>
          <ShippingConfigurationForm
            isOpen={drawerOpen}
            mode={drawerMode}
            initialValues={selectedConfiguration}
            onClose={closeDrawer}
            onSubmit={handleSubmit}
          />
        </CustomSheet>

        <DialogDelete
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setDeleteTarget(null);
          }}
          isDeleting={isDeleting}
          title={t('common:shipping_configurations_delete_title')}
          description={t('common:shipping_configurations_delete_description')}
          onDelete={handleDelete}
        />

        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mt-8">
          <div className="flex flex-col gap-2 max-w-2xl">
            <Title
              text={t('common:shipping_configurations_title')}
              className="text-xl md:text-2xl"
            />
            <p className="text-sm text-[#A1A1AA]">
              {t('common:shipping_configurations_description')}
            </p>
          </div>
          <Button
            data-cy="btn-create-shipping-config"
            variant="default"
            className="shrink-0 md:self-start"
            onClick={openCreate}
            disabled={isSaving || !canCreate}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" />
                {t('loading')}
              </span>
            ) : (
              <>
                <Plus />
                {t('common:shipping_configurations_create')}
              </>
            )}
          </Button>
        </div>

        <MotionDiv
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="w-full mt-6 overflow-x-auto">
            <TableListCustom
              emptyFallback={emptyFallback}
              headers={headers}
              isLoadingData={isLoadingList}
              data={tableData as Parameters<typeof TableListCustom>[0]['data']}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            >
              {configurations.map((configuration, index) => (
                <MotionTableRow
                  key={configuration.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * ROW_STAGGER_DELAY }}
                  className="h-[64px] border-b border-b-colorBorder hover:bg-transparent"
                  data-cy="shipping-config-row"
                >
                <TableCell className="font-medium">
                  {configuration.name || '—'}
                </TableCell>
                <TableCell>
                  <ShippingNamedItemsCell
                    items={configuration.products}
                    modalTitle={t('common:shipping_configurations_products_list')}
                    linkable
                  />
                </TableCell>
                <TableCell>
                  <ShippingNamedItemsCell
                    items={configuration.offers}
                    modalTitle={t('common:shipping_configurations_offers_list')}
                  />
                </TableCell>
                <TableCell>
                  <ShippingStringListCell
                    values={configuration.currencies}
                    modalTitle={t(
                      'common:shipping_configurations_currencies_list',
                    )}
                    allLabel={t('common:all')}
                    formatLabel={formatCurrencyLabel}
                    allWildcards={[APPLY_TO_ALL_CURRENCIES]}
                  />
                </TableCell>
                <TableCell>
                  <ShippingStringListCell
                    values={configuration.countries}
                    modalTitle={t(
                      'common:shipping_configurations_countries_list',
                    )}
                    allLabel={t('common:all')}
                    formatLabel={formatCountryLabel}
                    showCountryFlags
                  />
                </TableCell>
                <TableCell>{configuration.minQuantity}</TableCell>
                <TableCell>{configuration.amountFormatted}</TableCell>
                <TableCell>
                  <Badge
                    status={configuration.status ? 'active' : 'inactive'}
                  />
                </TableCell>
                <TableCell>
                  {(canEdit || canDelete) && (
                    <CTATable
                      items={[
                        ...(canEdit
                          ? [
                              {
                                id: 1,
                                label: t('edit'),
                                icon: <Edit />,
                                action: () => openEdit(configuration),
                              },
                            ]
                          : []),
                        ...(canDelete
                          ? [
                              {
                                id: 2,
                                label: t('checkouts:delete'),
                                icon: <Trash color="#F87171" />,
                                action: () => openDelete(configuration),
                              },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </TableCell>
                </MotionTableRow>
              ))}
            </TableListCustom>
          </Card>
        </MotionDiv>
      </div>
    </LayoutInside>
  );
}
